import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';

/*
  AamiraLuxe — Brand Identity Section
  ─────────────────────────────────────
  Left  : Jewelry image slides in from the left on scroll
  Right : "Aamira Luxe" branding + quote, same theme as ShopByReel
  No heading, no View All button
*/

const AamiraLuxe = () => {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: '-80px' });

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,600;0,700;1,400;1,600&family=Jost:wght@300;400;500;600&family=Cormorant+Garamond:ital,wght@0,300;0,400;1,300;1,400&display=swap');

        /* ── Section Shell ── */
        .al-section {
          background: #FAF8F4;
          overflow: hidden;
          padding: 90px clamp(24px, 6vw, 100px);
          position: relative;
        }

        /* subtle grain texture */
        .al-section::before {
          content: '';
          position: absolute;
          inset: 0;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='300' height='300'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.75' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='300' height='300' filter='url(%23n)' opacity='0.03'/%3E%3C/svg%3E");
          pointer-events: none;
          z-index: 0;
        }

        /* thin gold top border */
        .al-section::after {
          content: '';
          position: absolute;
          top: 0; left: clamp(24px, 6vw, 100px); right: clamp(24px, 6vw, 100px);
          height: 1px;
          background: linear-gradient(to right, transparent, #B08D57 30%, #B08D57 70%, transparent);
          opacity: 0.45;
        }

        /* ── Layout ── */
        .al-grid {
          position: relative;
          z-index: 1;
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: clamp(40px, 6vw, 90px);
          align-items: center;
          max-width: 1200px;
          margin: 0 auto;
        }

        /* ── Left: Image Block ── */
        .al-img-col {
          position: relative;
        }

        /* decorative gold frame offset behind image */
        .al-img-frame {
          position: absolute;
          top: 20px;
          left: -16px;
          width: 100%;
          height: 100%;
          border: 1px solid #B08D57;
          opacity: 0.3;
          border-radius: 2px;
          pointer-events: none;
        }

        .al-img-wrap {
          position: relative;
          width: 100%;
          aspect-ratio: 4/5;
          overflow: hidden;
          border-radius: 2px;
          box-shadow: 0 24px 60px rgba(28,26,24,0.14);
        }

        .al-img-wrap img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          object-position: center top;
          display: block;
          transition: transform 0.9s cubic-bezier(0.25, 0.46, 0.45, 0.94);
        }

        .al-img-wrap:hover img {
          transform: scale(1.04);
        }

        /* gold gradient overlay on image bottom */
        .al-img-wrap::after {
          content: '';
          position: absolute;
          inset: 0;
          background: linear-gradient(
            to top,
            rgba(176,141,87,0.22) 0%,
            transparent 50%
          );
          pointer-events: none;
        }

        /* floating tag on image */
        .al-img-tag {
          position: absolute;
          bottom: 28px;
          left: -20px;
          background: #1C1A18;
          color: #B08D57;
          font-family: 'Jost', sans-serif;
          font-size: 9px;
          font-weight: 600;
          letter-spacing: 0.22em;
          text-transform: uppercase;
          padding: 10px 18px;
          z-index: 3;
          white-space: nowrap;
        }

        /* ── Right: Brand Content ── */
        .al-content-col {
          display: flex;
          flex-direction: column;
          gap: 0;
          padding: 10px 0;
        }

        /* eyebrow label */
        .al-eyebrow {
          font-family: 'Jost', sans-serif;
          font-size: 10px;
          font-weight: 500;
          letter-spacing: 0.28em;
          text-transform: uppercase;
          color: #B08D57;
          display: flex;
          align-items: center;
          gap: 10px;
          margin-bottom: 20px;
        }
        .al-eyebrow::before {
          content: '';
          width: 28px;
          height: 1px;
          background: #B08D57;
          display: inline-block;
          flex-shrink: 0;
        }

        /* Brand name */
        .al-brand-name {
          font-family: 'Playfair Display', serif;
          font-size: clamp(42px, 5.5vw, 72px);
          font-weight: 700;
          color: #1C1A18;
          line-height: 0.95;
          letter-spacing: -0.02em;
          margin-bottom: 6px;
        }
        .al-brand-name em {
          display: block;
          font-style: italic;
          font-weight: 400;
          color: #B08D57;
          font-size: clamp(38px, 5vw, 66px);
        }

        /* thin gold divider */
        .al-divider {
          width: 56px;
          height: 1px;
          background: linear-gradient(to right, #B08D57, transparent);
          margin: 28px 0 30px;
        }

        /* Quote block */
        .al-quote-wrap {
          position: relative;
          padding-left: 22px;
          margin-bottom: 32px;
        }
        .al-quote-wrap::before {
          content: '';
          position: absolute;
          left: 0;
          top: 4px;
          bottom: 4px;
          width: 2px;
          background: linear-gradient(to bottom, #B08D57, rgba(176,141,87,0.2));
        }

        .al-quote-mark {
          font-family: 'Cormorant Garamond', serif;
          font-size: 72px;
          line-height: 0.6;
          color: #B08D57;
          opacity: 0.35;
          display: block;
          margin-bottom: 8px;
          font-weight: 300;
          letter-spacing: -0.02em;
        }

        .al-quote-text {
          font-family: 'Cormorant Garamond', serif;
          font-size: clamp(18px, 2.2vw, 24px);
          font-weight: 300;
          font-style: italic;
          color: #2E2B27;
          line-height: 1.65;
          letter-spacing: 0.01em;
        }

        .al-quote-attr {
          margin-top: 14px;
          font-family: 'Jost', sans-serif;
          font-size: 10px;
          font-weight: 500;
          letter-spacing: 0.2em;
          text-transform: uppercase;
          color: #B08D57;
        }

        /* sub-description */
        .al-desc {
          font-family: 'Jost', sans-serif;
          font-size: 13px;
          font-weight: 300;
          color: #6B6560;
          line-height: 1.9;
          letter-spacing: 0.03em;
          margin-bottom: 36px;
          max-width: 360px;
        }

        /* CTA row */
        .al-cta-row {
          display: flex;
          align-items: center;
          gap: 28px;
          flex-wrap: wrap;
        }

        .al-cta-primary {
          font-family: 'Jost', sans-serif;
          font-size: 10px;
          font-weight: 600;
          letter-spacing: 0.22em;
          text-transform: uppercase;
          color: #FAF8F4;
          background: #1C1A18;
          text-decoration: none;
          padding: 15px 34px;
          display: inline-block;
          transition: background 0.25s;
          position: relative;
          overflow: hidden;
        }
        .al-cta-primary::after {
          content: '';
          position: absolute;
          inset: 0;
          background: #B08D57;
          transform: translateX(-101%);
          transition: transform 0.3s cubic-bezier(0.4,0,0.2,1);
          z-index: 0;
        }
        .al-cta-primary:hover::after { transform: translateX(0); }
        .al-cta-primary span {
          position: relative;
          z-index: 1;
        }

        .al-cta-secondary {
          font-family: 'Jost', sans-serif;
          font-size: 10px;
          font-weight: 500;
          letter-spacing: 0.18em;
          text-transform: uppercase;
          color: #1C1A18;
          text-decoration: none;
          display: flex;
          align-items: center;
          gap: 8px;
          transition: color 0.2s;
        }
        .al-cta-secondary:hover { color: #B08D57; }
        .al-cta-secondary::after {
          content: '→';
          font-size: 12px;
          transition: transform 0.2s;
        }
        .al-cta-secondary:hover::after { transform: translateX(4px); }

        /* small gold ornament */
        .al-ornament {
          display: flex;
          align-items: center;
          gap: 10px;
          margin-top: 44px;
          opacity: 0.45;
        }
        .al-ornament span {
          font-family: 'Cormorant Garamond', serif;
          font-size: 11px;
          letter-spacing: 0.3em;
          text-transform: uppercase;
          color: #B08D57;
          font-style: italic;
        }
        .al-ornament::before,
        .al-ornament::after {
          content: '';
          flex: 1;
          height: 1px;
          background: linear-gradient(to right, transparent, #B08D57);
        }
        .al-ornament::after {
          background: linear-gradient(to left, transparent, #B08D57);
        }

        /* ── Responsive ── */
        @media (max-width: 768px) {
          .al-grid {
            grid-template-columns: 1fr;
            gap: 48px;
          }
          .al-img-col { max-width: 380px; margin: 0 auto; width: 100%; }
          .al-img-tag { left: 0; }
          .al-img-frame { left: 10px; }
          .al-section { padding: 60px clamp(20px, 5vw, 40px); }
          .al-desc { max-width: 100%; }
        }
      `}</style>

      <section className="al-section" ref={sectionRef}>
        <div className="al-grid">

          {/* ── LEFT: Image slides in from left ── */}
          <motion.div
            className="al-img-col"
            initial={{ x: -80, opacity: 0 }}
            animate={isInView ? { x: 0, opacity: 1 } : { x: -80, opacity: 0 }}
            transition={{ duration: 0.9, ease: [0.25, 0.46, 0.45, 0.94] }}
          >
            <div className="al-img-frame" />
            <div className="al-img-wrap">
              <img
                src="/1.jpg"
                alt="Aamira Luxe fine jewelry"
              />
            </div>
            <div className="al-img-tag">Fine Jewellery</div>
          </motion.div>

          {/* ── RIGHT: Brand content slides in from right ── */}
          <motion.div
            className="al-content-col"
            initial={{ x: 80, opacity: 0 }}
            animate={isInView ? { x: 0, opacity: 1 } : { x: 80, opacity: 0 }}
            transition={{ duration: 0.9, ease: [0.25, 0.46, 0.45, 0.94], delay: 0.12 }}
          >
            {/* eyebrow */}
            <motion.p
              className="al-eyebrow"
              initial={{ opacity: 0 }}
              animate={isInView ? { opacity: 1 } : { opacity: 0 }}
              transition={{ delay: 0.35, duration: 0.6 }}
            >
              Luxury Jewellery
            </motion.p>

            {/* Brand name */}
            <motion.h2
              className="al-brand-name"
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
              transition={{ delay: 0.42, duration: 0.7 }}
            >
              Aamira
              <em>Luxe</em>
            </motion.h2>

            <motion.div
              className="al-divider"
              initial={{ scaleX: 0, originX: 0 }}
              animate={isInView ? { scaleX: 1 } : { scaleX: 0 }}
              transition={{ delay: 0.6, duration: 0.6 }}
            />

            {/* Quote */}
            <motion.div
              className="al-quote-wrap"
              initial={{ opacity: 0, y: 16 }}
              animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 16 }}
              transition={{ delay: 0.7, duration: 0.7 }}
            >
              <span className="al-quote-mark">"</span>
              <p className="al-quote-text">
                Adorn yourself not merely with jewels,<br />
                but with the stories they carry — each piece
                a whisper of timeless elegance.
              </p>
              <p className="al-quote-attr">— Aamira Luxe</p>
            </motion.div>

            {/* Description */}
            <motion.p
              className="al-desc"
              initial={{ opacity: 0 }}
              animate={isInView ? { opacity: 1 } : { opacity: 0 }}
              transition={{ delay: 0.85, duration: 0.7 }}
            >
              Crafted for the woman who moves with grace and lives with intention.
              Every Aamira Luxe creation is a testament to heritage craftsmanship
              and contemporary refinement — jewellery that endures, like the memories it holds.
            </motion.p>

            {/* CTA row */}
            <motion.div
              className="al-cta-row"
              initial={{ opacity: 0, y: 12 }}
              animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 12 }}
              transition={{ delay: 0.98, duration: 0.6 }}
            >
              <a href="/collections/aamira-luxe" className="al-cta-primary">
                <span>Explore Collection</span>
              </a>
              <a href="/about" className="al-cta-secondary">
                Our Story
              </a>
            </motion.div>

            {/* ornament line */}
            <motion.div
              className="al-ornament"
              initial={{ opacity: 0 }}
              animate={isInView ? { opacity: 0.45 } : { opacity: 0 }}
              transition={{ delay: 1.1, duration: 0.8 }}
            >
              <span>Est. Heritage</span>
            </motion.div>

          </motion.div>
        </div>
      </section>
    </>
  );
};

export default AamiraLuxe;